import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import { HiArrowLeft, HiOutlineSave } from 'react-icons/hi';
import { HiXMark } from 'react-icons/hi2';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';
import CreatableComponent from '../../components/utils/Creatable';
import { uploadImageToS3, getFileExtension, deleteImageFromS3 } from '../../utils/s3Upload';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserFarms } from '../../store/features/userFarmsSlice';
import { bookingSchema } from '../../components/Forms/BookingSchema'; 

// --- Photo Uploader Component ---
const PhotoUploader = ({ label, fieldPath, currentUrls = [], setFieldValue, folder }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const handlePhotoCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => {
        const extension = getFileExtension(file);
        return uploadImageToS3(file, extension, folder);
      });

      const newUrls = await Promise.all(uploadPromises);
      const updatedUrls = [...currentUrls, ...newUrls];
      setFieldValue(fieldPath, updatedUrls);
    } catch (err) {
      console.error('Error uploading photos:', err);
      alert('Failed to upload some photos. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (indexToRemove) => {
    const urlToRemove = currentUrls[indexToRemove];
  
    if (deletingIndex === indexToRemove) return;
    
    setDeletingIndex(indexToRemove);
    try {
      await deleteImageFromS3(urlToRemove);
      const updatedUrls = currentUrls.filter((_, idx) => idx !== indexToRemove);
      setFieldValue(fieldPath, updatedUrls);
      
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo from server. Please try again.');
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <div className="border rounded p-3 bg-white text-center d-flex flex-column justify-content-start align-items-center mb-3 h-100">
      <h6 className="text-muted fw-bold mb-3">{label}</h6>
      
      {currentUrls.length > 0 && (
        <div className="d-flex flex-wrap justify-content-center gap-2 mb-3 w-100">
          {currentUrls.map((url, idx) => (
            <div key={idx} className="position-relative" style={{ width: '70px', height: '70px' }}>
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img 
                  src={url} 
                  alt={`${label} ${idx + 1}`} 
                  className={`w-100 h-100 object-fit-cover rounded border border-secondary ${deletingIndex === idx ? 'opacity-50' : ''}`} 
                />
              </a>
              {deletingIndex === idx ? (
                <div className="position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center" style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }}>
                   <Spinner animation="border" size="sm" variant="danger" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    removePhoto(idx);
                  }}
                  className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }}
                  title="Remove Photo"
                >
                  <HiXMark size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoCapture}
        className="d-none"
        disabled={uploading}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="btn btn-outline-success btn-sm w-100 py-2 mt-auto"
      >
        {uploading ? (
          <><Spinner as="span" animation="border" size="sm" className="me-2"/>Uploading...</>
        ) : (
          currentUrls.length > 0 ? '+ Add More' : '+ Upload Photos'
        )}
      </button>
    </div>
  );
};

const ViewEditBooking = () => {
  const { booking_id } = useParams();
  const navigate = useNavigate();
  const role = getRole();
  const dispatch = useDispatch();

  const [booking, setBooking] = useState(null);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSampleId, setSavingSampleId] = useState(null);
  const [cropOptions, setCropOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const { farms, loading: farmsLoading } = useSelector((state) => state.userFarms || { farms: [], loading: false });

  const farmSizeUnits = [
    "Acres", "Hectare", "Bigha", "Gaj", "Kanal", "Biswa", "Dismil", "Killa",
    "Marla", "Nali", "Guntha", "Square Karam", "Ankanam", "Gajam", "Chatak",
    "Dhur", "Perch", "Lessa", "Murabba", "Biswa Kacha", "Pura", "sq mtr"
  ];

  const treatmentOptions = [
    { value: "Not Applicable", label: "Not Applicable" },
    { value: "Initial Control", label: "Initial Control" },
    { value: "Initial Treated", label: "Initial Treated" },
    { value: "Final Control", label: "Final Control" },
    { value: "Final Treated", label: "Final Treated" },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const cropRes = await axiosInstance.get('/api/v2/recommendations/crops');
        if (cropRes.data.success) {
          setCropOptions(cropRes.data.data.map(c => ({ value: c.name_en, label: c.name_en })));
        }

        const endpoint = `/super-admin/bookings/${booking_id}`;
        const bookingRes = await axiosInstance.get(endpoint);
        
        if (bookingRes.data) {
          setBooking(bookingRes.data);
          const initializedSamples = (bookingRes.data.samples || []).map(s => ({
            ...s,
            sample_info: s.sample_info || { collection_photos: [], mixing_photos: [], packaging_photos: [] }
          }));
          setSamples(initializedSamples);

          if (bookingRes.data.user_id) {
            dispatch(fetchUserFarms({ id: bookingRes.data.user_id }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch booking details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [booking_id, dispatch]);

  const handleSampleChange = (index, field, value) => {
    const updatedSamples = [...samples];
    updatedSamples[index] = { ...updatedSamples[index], [field]: value };
    setSamples(updatedSamples);

    if (validationErrors[index]?.[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [index]: { ...prev[index], [field]: null }
      }));
    }
  };

  const handleSamplePhotoChange = (index, photoField, urlArray) => {
    const updatedSamples = [...samples];
    updatedSamples[index] = {
      ...updatedSamples[index],
      sample_info: {
        ...updatedSamples[index].sample_info,
        [photoField]: urlArray
      }
    };
    setSamples(updatedSamples);
  };

  const handleUpdateSample = async (index) => {
    const sampleData = samples[index];
    try {
      const dataToValidate = {
        samples: [{
          ...sampleData,
          test_type: sampleData.sample_type
        }]
      };

      await bookingSchema.validate(dataToValidate, { abortEarly: false });
      setValidationErrors((prev) => ({ ...prev, [index]: {} }));
      
    } catch (err) {
      if (err.name === 'ValidationError') {
        const errors = {};
        err.inner.forEach(e => {
          const fieldName = e.path.split('.').pop(); 
          errors[fieldName] = e.message;
        });
        
        setValidationErrors((prev) => ({ ...prev, [index]: errors }));
        return;
      }
    }

    setSavingSampleId(sampleData.sample_id);
    try {
      const payload = {
        sample_id: sampleData.sample_id,
        booking_id: booking.booking_id,
        farm_id: sampleData.farm_id,
        farm_name: sampleData.farm_name,
        farm_size: sampleData.farm_size,
        farm_size_unit: sampleData.farm_size_unit,
        previous_crop: sampleData.previous_crop,
        next_crop: sampleData.next_crop,
        age: sampleData.age,
        treatment: sampleData.treatment,
        comments: sampleData.comments,
        use_location_recommendation: sampleData.use_location_recommendation,
        sample_info: sampleData.sample_info
      };

      await axiosInstance.put('/super-admin/update-sample', payload);
      alert('Sample updated successfully');
    } catch (err) {
      console.error(err);
      alert('Failed to update sample');
    } finally {
      setSavingSampleId(null);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" variant="success" />
          <span className="ms-3 fw-bold text-muted">Loading Booking Details...</span>
        </div>
      </Wrapper>
    );
  }

  if (!booking) {
    return (
      <Wrapper>
        <div className="text-center py-5">
          <h3>Booking not found.</h3>
          <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="py-4 px-md-3">
        {/* Header */}
        <div className="d-flex align-items-center mb-4">
          <button className="btn btn-light me-3" onClick={() => navigate(-1)}>
            <HiArrowLeft size={20} />
          </button>
          <div>
            <h2 className="fw-bolder mb-0">Booking Details</h2>
            <p className="text-muted mb-0">ID: {booking.booking_id} | Created: {new Date(booking.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        <Row className="g-4">
          <Col lg={10} className="mx-auto">
            <h4 className="fw-bold mb-3">Associated Samples ({samples.length})</h4>
            
            {samples.map((sample, index) => {
              const errs = validationErrors[index] || {};

              return (
                <Card key={sample.sample_id} className="mb-5 shadow border-0">
                  <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3">
                    <h5 className="mb-0 fw-bold">
                      {sample.sample_type} Sample <span className="text-light ms-2 fs-6">({sample.sample_id})</span>
                    </h5>
                    <Badge bg="light" text="dark">{sample.sample_status}</Badge>
                  </Card.Header>
                  
                  <Card.Body className="p-4">
                    <Form>
                      <Row className="g-3">
                        <Col md={12}>
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <Form.Label className="fw-bold mb-0">Select Existing Farm (Optional)</Form.Label>
                          </div>
                          <Form.Select
                            className="border-primary bg-light"
                            disabled={farmsLoading}
                            value={sample.farm_id || ''}
                            onChange={(e) => {
                              const selectedFarmId = e.target.value;
                              
                              let updates = { farm_id: selectedFarmId };
                              if (selectedFarmId) {
                                const selectedFarm = farms.find(
                                  (f) => String(f.farm_id) === String(selectedFarmId) || String(f.id) === String(selectedFarmId)
                                );
                                if (selectedFarm) {
                                  updates.farm_name = selectedFarm.farm_name || selectedFarm.name || '';
                                  if (sample.sample_type === 'SOIL') {
                                    updates.farm_size = selectedFarm.farm_size || '';
                                    updates.farm_size_unit = selectedFarm.farm_size_unit || 'Acres';
                                  }
                                }
                              }

                              const updatedSamples = [...samples];
                              updatedSamples[index] = { ...updatedSamples[index], ...updates };
                              setSamples(updatedSamples);
                              setValidationErrors(prev => ({
                                ...prev, 
                                [index]: { ...prev[index], farm_name: null, farm_size: null, farm_size_unit: null }
                              }));
                            }}
                          >
                            <option value="">-- Choose Farm from your profile --</option>
                            {farms?.map((farm) => (
                              <option key={farm.farm_id || farm.id} value={farm.farm_id || farm.id}>
                                {farm.farm_name || farm.name} 
                                {farm.farm_size ? ` (${farm.farm_size} ${farm.farm_size_unit})` : ''}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>

                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="fw-bold">
                              {sample.sample_type === 'WATER' ? 'Water Sample Name' : 'Farm Name'}
                            </Form.Label>
                            <Form.Control 
                              type="text" 
                              value={sample.farm_name || ''} 
                              onChange={(e) => handleSampleChange(index, 'farm_name', e.target.value)} 
                              isInvalid={!!errs.farm_name}
                            />
                            <Form.Control.Feedback type="invalid">{errs.farm_name}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {sample.sample_type === 'SOIL' && (
                          <>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Farm Size</Form.Label>
                                <Form.Control 
                                  type="number" 
                                  step="0.1"
                                  value={sample.farm_size || ''} 
                                  onChange={(e) => handleSampleChange(index, 'farm_size', e.target.value)} 
                                  isInvalid={!!errs.farm_size}
                                />
                                <Form.Control.Feedback type="invalid">{errs.farm_size}</Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Unit</Form.Label>
                                <Form.Select 
                                  value={sample.farm_size_unit || 'Acres'} 
                                  onChange={(e) => handleSampleChange(index, 'farm_size_unit', e.target.value)}
                                  isInvalid={!!errs.farm_size_unit}
                                >
                                  {farmSizeUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errs.farm_size_unit}</Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Previous Crop</Form.Label>
                                <Form.Control 
                                  type="text" 
                                  value={sample.previous_crop || ''} 
                                  onChange={(e) => handleSampleChange(index, 'previous_crop', e.target.value)} 
                                  isInvalid={!!errs.previous_crop}
                                />
                                <Form.Control.Feedback type="invalid">{errs.previous_crop}</Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Next Crop</Form.Label>
                                {role === 'SUPPORT' ? (
                                  <>
                                    <div className={errs.next_crop ? "border border-danger rounded" : ""}>
                                      <CreatableComponent
                                        defaultOptions={cropOptions}
                                        placeholder={sample.next_crop || "Next Crop"}
                                        updateVal={(val) => handleSampleChange(index, 'next_crop', val)}
                                      />
                                    </div>
                                    {errs.next_crop && <div className="text-danger small mt-1">{errs.next_crop}</div>}
                                  </>
                                ) : (
                                  <Form.Control type="text" value={sample.next_crop || '-'} disabled readOnly />
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                  <Form.Label className="fw-bold">Treatment</Form.Label>
                                  <Form.Select 
                                    value={sample.treatment || ''} 
                                    onChange={(e) => handleSampleChange(index, 'treatment', e.target.value)}
                                    isInvalid={!!errs.treatment}
                                  >
                                    <option value="">Select Treatment</option>
                                    {treatmentOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Control.Feedback type="invalid">{errs.treatment}</Form.Control.Feedback>
                                </Form.Group>
                              </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-bold">Tree Age (Years)</Form.Label>
                                {role === 'SUPPORT' ? (
                                  <>
                                    <Form.Control 
                                      type="number" 
                                      step="0.1"
                                      min="0"
                                      value={sample.age ?? ''} 
                                      onChange={(e) => handleSampleChange(index, 'age', e.target.value)} 
                                      isInvalid={!!errs.age}
                                      placeholder="e.g. 1.5"
                                    />
                                    <Form.Control.Feedback type="invalid">{errs.age}</Form.Control.Feedback>
                                  </>
                                ) : (
                                  <Form.Control type="text" value={sample.age ?? '0'} disabled readOnly />
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={12}>
                              <Form.Check
                                type="switch"
                                id={`loc-rec-${sample.sample_id}`}
                                label="Enable Location-Based Recommendations"
                                checked={sample.use_location_recommendation || false}
                                onChange={(e) => handleSampleChange(index, 'use_location_recommendation', e.target.checked)}
                                className="mt-2 fw-bold text-success"
                              />
                            </Col>
                          </>
                        )}

                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="fw-bold">Comments</Form.Label>
                            <Form.Control 
                              as="textarea" 
                              rows={2} 
                              value={sample.comments || ''} 
                              onChange={(e) => handleSampleChange(index, 'comments', e.target.value)} 
                              isInvalid={!!errs.comments}
                            />
                            <Form.Control.Feedback type="invalid">{errs.comments}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Form>

                    <hr className="my-4" />

                    {/* --- PER-SAMPLE PHOTO UPLOADS --- */}
                    <h6 className="fw-bolder mb-3 text-success">
                      {sample.sample_type === 'WATER' ? 'Water Sample Photos' : 'Soil Sample Verification Photos'}
                    </h6>
                    <Row className="g-3">
                      <Col md={sample.sample_type === 'WATER' ? 12 : 4}>
                        <PhotoUploader
                          label={sample.sample_type === 'WATER' ? "Water Collection Photos" : "Sample Collection / Digging"}
                          fieldPath="collection_photos"
                          currentUrls={sample.sample_info?.collection_photos || []}
                          setFieldValue={(field, val) => handleSamplePhotoChange(index, field, val)}
                          folder="collection-photos/digging"
                        />
                      </Col>

                      {sample.sample_type === 'SOIL' && (
                        <>
                          <Col md={4}>
                            <PhotoUploader
                              label="Sample Mixing (Optional)"
                              fieldPath="mixing_photos"
                              currentUrls={sample.sample_info?.mixing_photos || []}
                              setFieldValue={(field, val) => handleSamplePhotoChange(index, field, val)}
                              folder="collection-photos/mixing"
                            />
                          </Col>
                          <Col md={4}>
                            <PhotoUploader
                              label="Sample Packaging / Bags"
                              fieldPath="packaging_photos"
                              currentUrls={sample.sample_info?.packaging_photos || []}
                              setFieldValue={(field, val) => handleSamplePhotoChange(index, field, val)}
                              folder="collection-photos/bag"
                            />
                          </Col>
                        </>
                      )}
                    </Row>

                  </Card.Body>
                  
                  <Card.Footer className="bg-light border-top-0 d-flex justify-content-end py-3 px-4">
                    <button 
                      className="btn btn-success btn-lg d-flex align-items-center"
                      onClick={() => handleUpdateSample(index)}
                      disabled={savingSampleId === sample.sample_id}
                    >
                      {savingSampleId === sample.sample_id ? (
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                      ) : (
                        <HiOutlineSave size={20} className="me-2" />
                      )}
                      Save Sample Data & Photos
                    </button>
                  </Card.Footer>
                </Card>
              );
            })}
          </Col>
        </Row>
      </div>
    </Wrapper>
  );
};

export default ViewEditBooking;